// =============================================================================
// navConfig — sections de la navigation regulateur + resolveur de route
//
// Consomme par Shell.tsx (rail des sections, colonne de la section, drawer
// mobile, command palette). Fonctions pures, testees dans __tests__/navConfig.test.ts.
// =============================================================================

import type { FC } from 'react'
import type { LucideIcon } from 'lucide-react'
import { BarChart3, Code2, Handshake, Network, Receipt, Settings, Truck } from 'lucide-react'
import {
  IconArrowsRepeat,
  IconBanknotes,
  IconBlocks,
  IconBuilding,
  IconBusinessIntelligence,
  IconClipboardCheck,
  IconClipboardList,
  IconCreditCard,
  IconCube,
  IconDocument,
  IconGrid,
  IconInbox,
  IconMap,
  IconShieldUser,
  IconSignal,
  IconStar,
  IconTag,
  IconUsers,
} from './navIcons'

export interface NavChild {
  label: string
  /** Chemin, eventuellement avec query string. */
  to: string
}

export interface NavItem {
  path: string
  label: string
  icon: FC
  /** Prefixes de route supplementaires rattaches a cet item (alias, sous-routes hors path). */
  match?: string[]
  beta?: boolean
  /** Page statique hors routeur, ouverte dans un nouvel onglet. */
  external?: boolean
  /** Troisieme niveau, affiche sous l'item quand il est actif. */
  children?: NavChild[]
}

export interface NavSection {
  id: string
  title: string
  icon: LucideIcon
  items: NavItem[]
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'vendre',
    title: 'Ventes',
    icon: Handshake,
    items: [
      { path: '/comptes',     label: 'Comptes', icon: IconBuilding,       match: ['/clients'] },
      { path: '/audits',      label: 'Audits',  icon: IconClipboardCheck },
      { path: '/flux',        label: 'Flux',    icon: IconArrowsRepeat },
    ],
  },
  {
    id: 'reseau',
    title: 'Réseau',
    icon: Network,
    items: [
      { path: '/partners',    label: 'Partenaires', icon: IconUsers },
      { path: '/catalogue',   label: 'Catalogue',   icon: IconTag },
      { path: '/exutoires',   label: 'Exutoires',   icon: IconBlocks },
      { path: '/routes',      label: 'Routes',      icon: IconMap },
      { path: '/supply-gaps', label: 'Gaps',        icon: IconInbox },
    ],
  },
  {
    id: 'operer',
    title: 'Opérations',
    icon: Truck,
    items: [
      { path: '/operations',   label: 'Regulation',   icon: IconGrid },
      { path: '/containers',   label: 'Contenants',   icon: IconCube },
      { path: '/documents',    label: 'Documents',    icon: IconDocument },
      { path: '/registre',     label: 'Registre',     icon: IconClipboardList },
      { path: '/avis-clients', label: 'Avis clients', icon: IconStar },
    ],
  },
  {
    id: 'facturer',
    title: 'Facturation',
    icon: Receipt,
    items: [
      { path: '/facturation',  label: 'Facturation',  icon: IconBanknotes, match: ['/cash', '/reglages-facturation'] },
      { path: '/prelevements', label: 'Prélèvements', icon: IconCreditCard },
    ],
  },
  {
    id: 'piloter',
    title: 'Pilotage',
    icon: BarChart3,
    items: [
      { path: '/business-intelligence', label: 'Business Intelligence', icon: IconBusinessIntelligence },
    ],
  },
  {
    id: 'reglages',
    title: 'Réglages',
    icon: Settings,
    items: [
      { path: '/dictionnaires', label: 'Dictionnaires', icon: IconTag },
      { path: '/users',         label: 'Utilisateurs',  icon: IconShieldUser, match: ['/users-v2'] },
    ],
  },
  {
    id: 'dev',
    title: 'Dev',
    icon: Code2,
    items: [
      { path: '/dev/e2e',      label: 'Carte produit', icon: IconBlocks },
      { path: '/admin/health', label: 'Health',        icon: IconSignal },
    ],
  },
]

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/')
}

/**
 * Item actif pour un pathname : prefixes = [item.path, ...item.match], le
 * prefixe matche le plus long gagne. Aucun match : null.
 */
export function resolveActive(pathname: string): { section: NavSection; item: NavItem } | null {
  let best: { section: NavSection; item: NavItem; length: number } | null = null
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      for (const prefix of [item.path, ...(item.match ?? [])]) {
        if (matchesPrefix(pathname, prefix) && (!best || prefix.length > best.length)) {
          best = { section, item, length: prefix.length }
        }
      }
    }
  }
  return best ? { section: best.section, item: best.item } : null
}

/**
 * Enfant actif d'un item pour pathname + search : `to` identique a
 * pathname + search (search vide et `to` sans query = chemin identique).
 * Aucun match : null.
 */
export function resolveChild(item: NavItem, pathname: string, search: string): NavChild | null {
  if (!item.children) return null
  const query = search && !search.startsWith('?') ? '?' + search : search
  const current = pathname + query
  return item.children.find(child => child.to === current) ?? null
}
